import { Component } from '@angular/core';
import { LoadingService } from '../../../services/loading.service';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { TranslateService } from '@ngx-translate/core';
import { DescriptionModel, TableColumn } from 'src/app/models/table-column.model';
import { Router } from '@angular/router';
import { TrainingCategoryOverviewModel } from 'src/app/models/training-category/training-category-overview.model';
import { TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-training-catalogue-overview',
  templateUrl: './training-catalogue-overview.component.html',
  styleUrls: ['./training-catalogue-overview.component.scss'],
  standalone: false
})
export class TrainingCatalogueOverviewComponent {
  searchString: string = '';

  allTrainingCategories: TrainingCategoryOverviewModel[] = [];
  trainingCategoriesBasic: TrainingCategoryModel[] = [];

  private _trainingCategories: TrainingCategoryOverviewModel[] = [];

  get trainingCategories(): TrainingCategoryOverviewModel[] {
    return this._trainingCategories;
  }

  set trainingCategories(trainingCategories: TrainingCategoryOverviewModel[]) {
    this._trainingCategories = trainingCategories;
    this.trainingCategoriesBasic = this.trainingCategories.map(
      (category: any) => category.trainingCategory
    ); // automatically update trainingCategoriesBasic
  }

  currentViewMode: 'widget' | 'list' = 'widget';
  availableViewModes: any[] = [
    { view: 'widget', icon: 'smalliconslayout' },
    { view: 'list', icon: 'menu' },
  ];

  trainingCatalogueColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-250',
      cellValue: (data: TrainingCategoryModel): string => {
        return data.name;
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DESCRIPTION'),
      dataField: 'description',
      cellTemplate: 'description',
      disableFiltering: true,
      cellValue: (data: TrainingCategoryModel): DescriptionModel => {
        return  {
          value: data.description
        };
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      cssClass: 'width-eq-70',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (catalogue: TrainingCategoryModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();
            this.navigateToCatalogueNewTab(catalogue.id);
          },
        },
      ],
    },
  ];

  private readonly pageStateKey = 'training-catalogue-page';
  readonly tableStateKey = 'training-catalogue-table';

  constructor(
    private trainingCategoryService: TrainingCategoryService,
    private loaderService: LoadingService,
    private translateService: TranslateService,
    private router: Router,
    private pageStateService: PageStateService
  ) {
    this.loadTrainingCategories();
  }


  ngOnInit() {
    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreTrainingCategoriesOverview
    )) {
      this.pageStateService.clear(this.tableStateKey);
      this.pageStateService.clear(this.pageStateKey);
    }
  }

  loadTrainingCategories() {
    this.loaderService
      .load(this.trainingCategoryService.getAllTrainingCategoriesOverview())
      .subscribe((trainingCategories) => {
        this.allTrainingCategories = trainingCategories.filter(
          (category: TrainingCategoryOverviewModel) =>
            !!category.courses.length || !!category.trainings.length
        );
        this.trainingCategories = [...this.allTrainingCategories];
        this.loadPageState();
      });
  }

  onSearchInput(event: any) {
    this.searchString = event.value.toLowerCase();
    this.savePageState();

    this.searchInput();
  }

  private searchInput() {
    if (!!this.searchString) {
      this.trainingCategories = this.allTrainingCategories.filter(
        (obj: TrainingCategoryOverviewModel) => {
          const categoryMatch =
            obj.trainingCategory.name
              .toLowerCase()
              .includes(this.searchString) ||
            (obj.trainingCategory.description || '')
              .toLowerCase()
              .includes(this.searchString);

          const coursesMatch = obj.courses?.some((course: any) =>
            course.name?.toLowerCase().includes(this.searchString)
          );

          const trainingsMatch = obj.trainings?.some((training: any) =>
            training.name?.toLowerCase().includes(this.searchString)
          );

          return categoryMatch || coursesMatch || trainingsMatch;
        }
      );
    } else {
      this.trainingCategories = [...this.allTrainingCategories];
    }
  }

  onViewModeChange(event: any) {
    this.currentViewMode = event.itemData.view;
    this.savePageState();
  }

  navigateToCatalogueNewTab(id: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['training-catalogue', id])
    );

    window.open(url, '_blank');
  }

  navigateToCatalogue(id?: string) {
    this.router.navigate(['training-catalogue', id]);
  }

  private savePageState(): void {
    this.pageStateService.save(this.pageStateKey, {
      searchString: this.searchString,
      currentViewMode: this.currentViewMode
    });
  }
  
  private loadPageState(): void {
    const state = this.pageStateService.load<{
      searchString: string;
      currentViewMode: "widget" | "list";
    }>(this.pageStateKey);

    if (!state) return;

    this.searchString = state.searchString ?? '';
    this.currentViewMode = state.currentViewMode ?? 'widget';
    this.searchInput();
  }
}
